// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import { tagsApi } from '../api/tagsApi';
import { useTags } from '../contexts/TagsContext';
import type { TagDTO, TaggableEntityType } from '../types/tag';

/**
 * Editable tag-set controller for a single entity's detail-page chip group +
 * picker affordance. Uses the replace-set endpoint (PUT
 * /api/tags/assignments/{entityType}/{entityId}) so the caller can pass the
 * TagPicker's toggled selection straight through — the server computes the
 * add/remove delta and audits it as the ASSIGN/UNASSIGN events (Tags PRD
 * §8.2). Optimistic update with rollback on failure.
 */
export function useEntityTagEditor(entityType: TaggableEntityType, entityId: string, initialTags: TagDTO[]) {
  const { createTag } = useTags();
  const [tags, setTags] = useState<TagDTO[]>(initialTags);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setInitial = useCallback((next: TagDTO[]) => setTags(next), []);

  const replace = useCallback(async (tagIds: string[], resolveTag: (id: string) => TagDTO | undefined) => {
    const previous = tags;
    const optimistic = tagIds
      .map((id) => resolveTag(id))
      .filter((t): t is TagDTO => !!t);
    setTags(optimistic);
    setSaving(true);
    setError(null);
    try {
      const result = await tagsApi.replace(entityType, entityId, tagIds);
      setTags(result.tags);
    } catch (err) {
      setTags(previous);
      setError(err instanceof Error ? err.message : 'Failed to update tags');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [entityType, entityId, tags]);

  /** Create a brand-new tag inline (TagPicker "Create '<text>'" row) then assign it to this entity. */
  const createAndAssign = useCallback(async (name: string) => {
    setSaving(true);
    setError(null);
    try {
      const created = await createTag({ name });
      const result = await tagsApi.assign(entityType, entityId, created.id);
      setTags(result.tags);
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [createTag, entityType, entityId]);

  return { tags, setInitial, replace, createAndAssign, saving, error };
}
